import { Link } from 'react-router-dom';

import styled, { css, DefaultTheme } from 'styled-components';

import media from 'apps/promo/theme/media';

export type DiskButtonVariant = 'primary' | 'secondary' | 'secondary_light' | 'tertiary' | 'tertiary_light' | 'error' | 'errorHint';
export type DiskButtonSize = 'small' | 'tight' | 'fit';

interface ButtonStyleProps {
    variant?: DiskButtonVariant;
    size?: DiskButtonSize;
    disabled?: boolean;
    $isInverted?: boolean;
    raised?: boolean | string;
    $marginRight?: string;
    $marginTop?: string;
}

type DiskButtonTheme = DefaultTheme & {
    purple_24_tr: string;
    purple_100: string;
    white_24_tr_base: string;
    white: string;
    purple_72_lc: string;
    white_40_tr_base: string;
    purple_8_tr: string;
    grey_16_lc: string;
    pink_8_tr: string;
    pink_16_tr: string;
    grey_8_lc: string;
    purple_16_tr: string;
    shadow_grey: string;
    purple_24_lc: string;
};

const getTheme = (props: { theme: DefaultTheme }): DiskButtonTheme => props.theme as DiskButtonTheme;
const shouldForwardDiskButtonProp = (prop: string) => !['variant', 'size', 'raised', '$isInverted', '$marginRight', '$marginTop'].includes(prop);

// Default variant - tertiary
const getBackground = (props: ButtonStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    switch (props.variant) {
        case 'primary':
            return props.disabled ? theme.purple_24_tr : theme.purple_100;
        case 'secondary':
            return props.$isInverted ? theme.white_24_tr_base : theme.white;
        case 'secondary_light':
            return 'transparent';
        case 'tertiary':
            return theme.white;
        case 'tertiary_light':
            return 'transparent';
        case 'error':
            return theme.pink_8_tr;
        case 'errorHint':
            return theme.white;
        default:
            return theme.white;
    }
};

const getHoverBackground = (props: ButtonStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    switch (props.variant) {
        case 'primary':
            return props.disabled ? theme.purple_24_tr : theme.purple_72_lc;
        case 'secondary':
            return props.$isInverted ? theme.white_40_tr_base : theme.purple_8_tr;
        case 'secondary_light':
            return theme.white_40_tr_base;
        case 'tertiary':
            return theme.grey_16_lc;
        case 'tertiary_light':
            return theme.white;
        case 'error':
            return theme.pink_16_tr;
        case 'errorHint':
            return theme.pink_8_tr;
        default:
            return theme.grey_16_lc;
    }
};

const getActiveBackground = (props: ButtonStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    switch (props.variant) {
        case 'primary':
            return props.disabled ? theme.purple_24_tr : theme.purple_72_lc;
        case 'secondary':
            return props.$isInverted ? theme.white_40_tr_base : theme.purple_8_tr;
        case 'tertiary':
            return theme.grey_8_lc;
        case 'tertiary_light':
            return theme.white;
        case 'error':
            return theme.pink_16_tr;
        case 'errorHint':
            return theme.pink_8_tr;
        default:
            return theme.grey_8_lc;
    }
};

const getBoxShadow = (props: ButtonStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    const raised = props.raised && props.raised.toString() === 'true' && !props.disabled;
    if (props.variant === 'primary') {
        return raised ? `0px 8px 8px ${theme.purple_16_tr}` : 'none';
    }
    return raised ? `${theme.shadow_grey}` : 'none';
};

const getSize = (props: ButtonStyleProps) => {
    switch (props.size) {
        case 'small':
            return '28px';
        case 'tight':
            return '40px';
        case 'fit':
            return '56px';
        default:
            return '40px';
    }
};

const getDesktopSize = (props: ButtonStyleProps) => {
    switch (props.size) {
        case 'small':
            return '32px';
        case 'tight':
            return '56px';
        case 'fit':
            return '112px';
        default:
            return '56px';
    }
};

const CommonCss = css<ButtonStyleProps>`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => getBackground(props)};
    border: none;
    border-radius: 50%;
    width: ${(props) => getSize(props)};
    height: ${(props) => getSize(props)};
    padding: 0;
    box-shadow: ${(props) => getBoxShadow(props)};
    pointer-events: auto;
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    margin-right: ${(props) => (props.$marginRight ? props.$marginRight : null)};
    margin-top: ${(props) => (props.$marginTop ? props.$marginTop : null)};

    &:hover {
        background-color: ${(props) => getHoverBackground(props)};
    }

    &:active {
        background-color: ${(props) => getActiveBackground(props)};
    }

    &:focus {
        background-color: ${(props) => getActiveBackground(props)};
    }

    &:focus-visible {
        outline: 1px dashed ${(props) => getTheme(props).purple_24_lc};
    }

    ${media.desktop1920} {
        width: ${(props) => getDesktopSize(props)};
        height: ${(props) => getDesktopSize(props)};
    }
`;

export const CommonButton = styled.button.withConfig({ shouldForwardProp: shouldForwardDiskButtonProp })<ButtonStyleProps>`
    ${CommonCss};
`;
export const LinkButton = styled.a.withConfig({ shouldForwardProp: shouldForwardDiskButtonProp })<ButtonStyleProps>`
    ${CommonCss};
`;
export const ReactLinkButton = styled(Link).withConfig({ shouldForwardProp: shouldForwardDiskButtonProp })<ButtonStyleProps>`
    ${CommonCss};
`;
